import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faArrowRight, faExclamationCircle, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { salmon } from 'lib/css/colors';
import Select from 'react-select';

import { areYouSure } from 'lib/utils/toast';
import {
  ATTRIBUTE_VALUES,
  DATA_SOURCE_AUTO_MAP,
  DATA_SOURCE_UPDATE,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import pointerOnHover from 'lib/css/pointerOnHover';
import SwalDialog from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import usePut from 'lib/hooks/usePut';

function AttributeValueMappingModal({
  selectedOption,
  sourceAttribute,
  onHide,
  dataSource,
  refetch,
}) {
  const existingColumnValueMap = dataSource?.column_value_map?.[sourceAttribute] || {};

  const [valueMappingToDisplay, setValueMappingToDisplay] = useState(
    Object.keys(existingColumnValueMap).map(columnValue => ({
      [columnValue]: {
        fsa_attribute_value: existingColumnValueMap[columnValue],
        auto_mapped: 0,
      },
    })),
  );

  const { data: { attribute_values } = {}, loading } = useGet(
    ATTRIBUTE_VALUES,
    {
      ...(selectedOption.attributeID && { attribute_id: selectedOption.attributeID }),
      ...(selectedOption.globalAttributeID && {
        global_attribute_id: selectedOption.globalAttributeID,
      }),
    },
    !selectedOption.attributeID && !selectedOption.globalAttributeID,
  );

  const { data: { updated_column_value_mapping } = {}, refetch: refetchColumnValues } = useGet(
    DATA_SOURCE_AUTO_MAP,
    {
      mapping: JSON.stringify(valueMappingToDisplay), //to encode the array
      fsa_attribute: selectedOption.label,
    },
  );

  const { putData: updateDataSource } = usePut(DATA_SOURCE_UPDATE, () => {
    refetch();
    onHide();
    SwalDialog('success', `Successfully Updated the Column Value Map`, 'Success', 'center');
  });

  const attributeValueOptions = attribute_values?.map(value => ({
    value: value.attribute_value,
    label: value.attribute_value,
  }));

  const handleColumnValueMapChange = (source, option, index, mapping) => {
    setValueMappingToDisplay(prevState => {
      const updatedMap = [...prevState];
      updatedMap[index] = {
        [source]: {
          ...mapping,
          fsa_attribute_value: option?.value || null,
          auto_mapped: 0,
        },
      };
      return updatedMap;
    });
  };

  const handleSourceValueChange = (index, newSource, mapping) => {
    setValueMappingToDisplay(prevState => {
      const updatedMap = [...prevState];
      updatedMap[index] = { [newSource]: mapping };
      return updatedMap;
    });
  };

  const handleAddRow = () => {
    setValueMappingToDisplay(prevState => [
      ...prevState,
      { '': { fsa_attribute_value: null, auto_mapped: 0, is_manual: true } },
    ]);
  };

  const handleRemoveRow = index => {
    setValueMappingToDisplay(prevState => prevState.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleSave = () => {
    const derivedMap = {};
    const duplicateSources = new Set();
    for (const item of valueMappingToDisplay) {
      const [rawSource, mapping] = Object.entries(item)[0];
      const source = rawSource.trim();
      const hasFsaValue = Boolean(mapping.fsa_attribute_value);

      if (!source && !hasFsaValue) {
        continue;
      }

      if (mapping.is_manual && (!source || !hasFsaValue)) {
        SwalDialog(
          'warning',
          'Every added row needs both a Source Attribute Value and an FSA Attribute Value before saving. Please complete or remove the incomplete row.',
          'Incomplete Mapping',
          'center',
        );
        return;
      }

      if (source in derivedMap) {
        duplicateSources.add(source);
      }
      derivedMap[source] = mapping.fsa_attribute_value;
    }

    const commit = () => {
      updateDataSource({
        data_source_id: dataSource.id,
        column_value_map: {
          ...dataSource?.column_value_map,
          [sourceAttribute]: derivedMap,
        },
      });
    };

    if (duplicateSources.size > 0) {
      areYouSure(
        commit,
        `"${[...duplicateSources][0]}" appears more than once in this list. ` +
          "Continuing will keep only the last row's mapping for that value.",
      );
    } else {
      areYouSure(commit, 'Are you sure You want to Save The Column Value Mapping?');
    }
  };

  return (
    <Modal size="xl" show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Map Source Attribute Values</Modal.Title>
        <Button
          className="ml-2"
          variant="outline-primary"
          onClick={() => {
            refetchColumnValues();
            if (updated_column_value_mapping && Array.isArray(updated_column_value_mapping)) {
              setValueMappingToDisplay([...updated_column_value_mapping]);
            }
          }}
        >
          Auto Map
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Table striped>
          <thead>
            <tr>
              <th>Source Attribute Values</th>
              <th />
              <th />
              <th>FSA Attribute Values</th>
              <th />
            </tr>
          </thead>
          {loading ? (
            <div className={css(styles.spinnerOverlay)}>
              <LoadingSpinner />
            </div>
          ) : (
            <tbody>
              {valueMappingToDisplay.map((item, index) => {
                const [source, mapping] = Object.entries(item)[0];
                return (
                  <tr key={index}>
                    <td>
                      <Form.Control
                        type="text"
                        value={source}
                        onChange={event =>
                          handleSourceValueChange(index, event.target.value, mapping)
                        }
                      />
                    </td>
                    <td>
                      <FontAwesomeIcon icon={faArrowRight} />
                    </td>
                    <td>
                      {mapping.auto_mapped ? (
                        <OverlayTrigger overlay={<Tooltip>Auto mapped</Tooltip>}>
                          <FontAwesomeIcon icon={faExclamationCircle} color={salmon} />
                        </OverlayTrigger>
                      ) : null}
                    </td>
                    <td>
                      <Select
                        value={
                          attributeValueOptions?.find(
                            option => option.value === mapping.fsa_attribute_value,
                          ) || null
                        }
                        options={attributeValueOptions}
                        onChange={option =>
                          handleColumnValueMapChange(source, option, index, mapping)
                        }
                        isClearable
                        classNamePrefix="react-select"
                      />
                    </td>
                    <td>
                      <FontAwesomeIcon
                        className={pointerOnHover}
                        icon={faTrashAlt}
                        onClick={() => handleRemoveRow(index)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </Table>
        <Button variant="outline-primary" size="sm" onClick={handleAddRow}>
          + Add Value
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleSave}>Save Source Attrubute Value Mappings</Button>
      </Modal.Footer>
    </Modal>
  );
}

const styles = StyleSheet.create({
  spinnerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
});

AttributeValueMappingModal.propTypes = {
  onHide: PropTypes.func,
  selectedOption: PropTypes.object,
  sourceAttribute: PropTypes.string,
  dataSource: PropTypes.object,
  refetch: PropTypes.func,
};

export default AttributeValueMappingModal;
